import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";

function isIdSegment(segment) {
  const s = String(segment || "");
  if (!s) return false;
  const looksLikeUuid =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s);
  const looksLikeCuid = /^c[a-z0-9]{10,}$/i.test(s);
  const looksLikeLongId = s.length >= 16 && /^[a-z0-9_-]+$/i.test(s);
  return looksLikeUuid || looksLikeCuid || looksLikeLongId;
}

function crumbLabel(segment) {
  if (!segment) return "Home";
  if (segment === "carrier") return "Carrier";
  if (segment === "broker") return "Broker";
  if (segment === "assigned") return "Assigned Loads";
  if (segment === "board") return "Load Board";
  if (segment === "documents") return "Documents";
  if (segment === "pending") return "Pending Review";
  if (segment === "loads") return "Loads";
  if (segment === "shipper") return "Shipper";
  if (isIdSegment(segment)) return "Details";
  return segment;
}

function shortLabel(s, max = 28) {
  const str = String(s || "").trim();
  if (!str) return "";
  return str.length > max ? str.slice(0, max - 1) + "…" : str;
}

export default function PageHeader({ title, subtitle, detailLabel }) {
  const loc = useLocation();
  const navigate = useNavigate();
  const { role, adminEmail, carrierEmail, shipperEmail, setIsAuthenticated } = useAuth();
  
  const parts = loc.pathname.split("/").filter(Boolean);
  const lastIdx = parts.length - 1;
  const lastPart = parts[lastIdx] || "";
  const lastIsId = isIdSegment(lastPart);
  
  const crumbs = parts.map((p, idx) => {
    const isLast = idx === lastIdx;
    const isSecondLast = idx === lastIdx - 1;
    const disableLink = isLast || (lastIsId && isSecondLast);
    let label = crumbLabel(p);
    
    if (lastIsId && isLast && detailLabel) {
      label = shortLabel(detailLabel);
    }
    
    return {
      label,
      to: "/" + parts.slice(0, idx + 1).join("/"),
      disableLink,
    };
  });

  // Determine current user email based on role
  const currentEmail = role === "BROKER" 
    ? adminEmail 
    : role === "CARRIER" 
    ? carrierEmail 
    : shipperEmail;

  // Extract name from email (before @)
  const displayName = currentEmail ? currentEmail.split("@")[0] : "";

  function handleLogout() {
    setIsAuthenticated(false);
    navigate("/login");
  }

  return (
    <div style={{ marginBottom: 18 }}>
      {/* Top row: Breadcrumbs on left, User info on right */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        {/* Breadcrumbs */}
        <div style={{ fontSize: 12, color: "#666" }}>
          <Link to="/" style={{ color: "#666", textDecoration: "none" }}>
            Home
          </Link>
          {crumbs.map((c) => (
            <span key={c.to}>
              {" "}
              /{" "}
              {c.disableLink ? (
                <span style={{ fontWeight: 700, color: "#333" }}>{c.label}</span>
              ) : (
                <Link to={c.to} style={{ color: "#666", textDecoration: "none" }}>
                  {c.label}
                </Link>
              )}
            </span>
          ))}
        </div>

        {/* User info and logout */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 12, color: "#666" }}>
            <span style={{ fontWeight: 600, color: "#333" }}>{displayName}</span>
            <span style={{ margin: "0 4px" }}>•</span>
            <span>{currentEmail}</span>
          </div>
          <button
            onClick={handleLogout}
            style={{
              padding: "4px 12px",
              background: "#ef4444",
              color: "white",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Page title */}
      <div style={{ fontSize: 22, fontWeight: 900 }}>{title}</div>
      {subtitle ? <div style={{ fontSize: 13, color: "#555", marginTop: 4 }}>{subtitle}</div> : null}
    </div>
  );
}
